import { Box, Icon, Table, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import {
  TOTAL_DAY_MINUTES,
  WIDTH_PROJECTS_COLUMN,
  WIDTH_WEEKDAYS_COLUMN,
  type maxMinutesPerCurrentWeekTypes,
} from 'modules/project_management/lib/constants'
import { formatProjectHours } from 'modules/project_management/lib/formatHours'

interface FooterProps {
  maxMinutesPerCurrentWeek?: { minutes: number; type: maxMinutesPerCurrentWeekTypes }[]
  totalDayInputedMinutes: number[]
}

const ComparativeCell = ({
  totalDayMinutes,
  maxMinutes,
  type,
}: {
  totalDayMinutes: number
  maxMinutes: number
  type: maxMinutesPerCurrentWeekTypes
}) => {
  const [toggle, setToggle] = useToggle(false)
  const showFullComparison = totalDayMinutes > 0
  const getTooltipMessage = () => {
    if (maxMinutes < totalDayMinutes) {
      switch (type) {
        case 'estimated':
          return i18n.t(
            'project_management.my_projects.table.footer_tooltips.estimated_hours_exceeded',
            {
              exceeded_hours: formatProjectHours(totalDayMinutes - maxMinutes),
            }
          )
        case 'worked':
          return i18n.t(
            'project_management.my_projects.table.footer_tooltips.worked_hours_exceeded',
            {
              exceeded_hours: formatProjectHours(totalDayMinutes - maxMinutes),
            }
          )
      }
    } else {
      return `${formatProjectHours(totalDayMinutes)} / ${formatProjectHours(maxMinutes)}`
    }
  }
  return (
    <Table.Cell paddingY="s8" paddingX="s8" colSpan={WIDTH_WEEKDAYS_COLUMN}>
      <Tooltip.Alt
        open={showFullComparison && toggle}
        toggle={setToggle}
        attachment="top center"
        label={getTooltipMessage()}
      >
        <Box flexDirection="row" justifyContent="center" alignItems="center">
          <Text size="200" weight="medium" ellipsis>
            {formatProjectHours(totalDayMinutes)} /{formatProjectHours(maxMinutes)}
          </Text>
          {maxMinutes < totalDayMinutes && (
            <Box width="s12">
              <Icon.Small color="tangerine1000" icon="alert-circle" />
            </Box>
          )}
        </Box>
      </Tooltip.Alt>
    </Table.Cell>
  )
}

const Cell = ({ totalDayMinutes }: { totalDayMinutes: number }) => {
  const [toggle, setToggle] = useToggle(false)
  const showWarning = totalDayMinutes > TOTAL_DAY_MINUTES

  return (
    <Table.Cell paddingY="s8" paddingX="s8" colSpan={WIDTH_WEEKDAYS_COLUMN}>
      <Tooltip.Alt
        open={showWarning && toggle}
        toggle={setToggle}
        attachment="top center"
        label={i18n.t('project_management.my_projects.table.footer_tooltips.day_hours_exceeded')}
      >
        <Box flexDirection="row" justifyContent="center" alignItems="center">
          <Text size="200" weight="medium" ellipsis>
            {formatProjectHours(totalDayMinutes)}
          </Text>
          {showWarning && (
            <Box width="s12">
              <Icon.Small color="tangerine1000" icon="alert-circle" />
            </Box>
          )}
        </Box>
      </Tooltip.Alt>
    </Table.Cell>
  )
}

const Footer = ({ maxMinutesPerCurrentWeek, totalDayInputedMinutes }: FooterProps) => {
  return (
    <Table.Row background="grey200">
      <Table.Cell paddingX="s16" width={40} />
      <Table.Cell paddingY="s8" colSpan={WIDTH_PROJECTS_COLUMN} />

      {totalDayInputedMinutes.map((totalDayMinutes, index: number) => {
        const cell = <Cell key={index} totalDayMinutes={totalDayMinutes} />

        if (!maxMinutesPerCurrentWeek) return cell

        const currentDay = maxMinutesPerCurrentWeek[index]
        if (!currentDay) return cell

        return (
          <ComparativeCell
            key={index}
            maxMinutes={currentDay.minutes}
            totalDayMinutes={totalDayMinutes}
            type={currentDay.type}
          />
        )
      })}
    </Table.Row>
  )
}

export default Footer
